import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { 
  Sparkles, ChevronRight, MapPin, X, Leaf, RefreshCw, 
  Flame, Zap, Heart, ArrowRight, Check, Loader2
} from 'lucide-react';
import { useTranslation } from '../i18n';

// Storage keys
const ONBOARDING_COMPLETED_KEY = 'motivya_onboarding_completed';
const USER_OBJECTIVE_KEY = 'motivya_user_objective';

const objectivesDef = [
  { id: 'douceur', labelKey: 'objectives.douceur', emoji: '🌱', color: 'green', descKey: 'objectives.douceurDesc', icon: Leaf },
  { id: 'regulier', labelKey: 'objectives.regulier', emoji: '🔄', color: 'blue', descKey: 'objectives.regulierDesc', icon: RefreshCw },
  { id: 'challenge', labelKey: 'objectives.challenge', emoji: '🔥', color: 'orange', descKey: 'objectives.challengeDesc', icon: Flame },
  { id: 'jeune', labelKey: 'objectives.jeune', emoji: '⚡', color: 'purple', descKey: 'objectives.jeuneDesc', icon: Zap },
  { id: 'senior', labelKey: 'objectives.senior', emoji: '🌿', color: 'teal', descKey: 'objectives.seniorDesc', icon: Heart },
];

// Check if onboarding should show
export const shouldShowOnboarding = () => {
  return !localStorage.getItem(ONBOARDING_COMPLETED_KEY);
};

// Mark onboarding as completed
export const completeOnboarding = (objective) => {
  localStorage.setItem(ONBOARDING_COMPLETED_KEY, 'true');
  if (objective) {
    localStorage.setItem(USER_OBJECTIVE_KEY, objective);
  }
};

// Reset onboarding (for testing)
export const resetOnboarding = () => {
  localStorage.removeItem(ONBOARDING_COMPLETED_KEY);
  localStorage.removeItem(USER_OBJECTIVE_KEY);
};

export default function OnboardingModal({ isOpen, onClose, onComplete }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [selectedObjective, setSelectedObjective] = useState(null);
  const [isClosing, setIsClosing] = useState(false);
  const [isRequestingLocation, setIsRequestingLocation] = useState(false);

  const totalSteps = 2;

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      onClose?.();
    }, 300);
  };

  const handleSkip = () => {
    completeOnboarding(null);
    handleClose();
  };

  const handleNext = () => {
    if (step < totalSteps) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const requestGeolocation = () => {
    setIsRequestingLocation(true);
    
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          // Success - save position and complete
          const { latitude, longitude } = position.coords;
          localStorage.setItem('motivya_user_lat', latitude.toString());
          localStorage.setItem('motivya_user_lng', longitude.toString());
          setIsRequestingLocation(false);
          handleCompleteOnboarding();
        },
        (error) => {
          // Error or denied - continue anyway
          console.log('Geolocation denied or error:', error);
          setIsRequestingLocation(false);
          handleCompleteOnboarding();
        },
        { timeout: 10000, enableHighAccuracy: false }
      );
    } else {
      // Geolocation not supported
      setIsRequestingLocation(false);
      handleCompleteOnboarding();
    }
  };

  const handleCompleteOnboarding = () => {
    completeOnboarding(selectedObjective);
    onComplete?.(selectedObjective);
    handleClose();
    navigate('/explore');
  };

  const handleComplete = () => {
    // Request geolocation before completing
    requestGeolocation();
  };

  const handleObjectiveSelect = (objectiveId) => {
    setSelectedObjective(objectiveId);
  };

  const colorClasses = {
    green: {
      bg: 'bg-green-500/10',
      border: 'border-green-500/30',
      selected: 'bg-green-500/20 border-green-500 ring-2 ring-green-500/50',
      text: 'text-green-400',
      glow: 'shadow-green-500/20'
    },
    blue: {
      bg: 'bg-blue-500/10',
      border: 'border-blue-500/30',
      selected: 'bg-blue-500/20 border-blue-500 ring-2 ring-blue-500/50',
      text: 'text-blue-400',
      glow: 'shadow-blue-500/20'
    },
    orange: {
      bg: 'bg-orange-500/10',
      border: 'border-orange-500/30',
      selected: 'bg-orange-500/20 border-orange-500 ring-2 ring-orange-500/50',
      text: 'text-orange-400',
      glow: 'shadow-orange-500/20'
    },
    purple: {
      bg: 'bg-purple-500/10',
      border: 'border-purple-500/30',
      selected: 'bg-purple-500/20 border-purple-500 ring-2 ring-purple-500/50',
      text: 'text-purple-400',
      glow: 'shadow-purple-500/20'
    },
    teal: {
      bg: 'bg-teal-500/10',
      border: 'border-teal-500/30',
      selected: 'bg-teal-500/20 border-teal-500 ring-2 ring-teal-500/50',
      text: 'text-teal-400',
      glow: 'shadow-teal-500/20'
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: isClosing ? 0 : 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      >
        {/* Backdrop */}
        <motion.div 
          className="absolute inset-0 bg-black/80 backdrop-blur-md"
          onClick={handleSkip}
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: isClosing ? 0 : 1, scale: isClosing ? 0.9 : 1, y: isClosing ? 20 : 0 }}
          transition={{ type: 'spring', damping: 25, stiffness: 300 }}
          className="relative w-full max-w-2xl bg-gradient-to-b from-card to-background border border-white/10 rounded-3xl shadow-2xl overflow-hidden"
          data-testid="onboarding-modal"
        >
          {/* Close button */}
          <button
            onClick={handleSkip}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/10 text-white/50 hover:text-white transition-colors z-10"
            data-testid="onboarding-skip"
          >
            <X className="w-5 h-5" />
          </button>

          {/* Progress bar */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-white/10">
            <motion.div
              className="h-full bg-primary"
              initial={{ width: 0 }}
              animate={{ width: `${(step / totalSteps) * 100}%` }}
              transition={{ duration: 0.3 }}
            />
          </div>

          {/* Content */}
          <div className="p-8 pt-12">
            <AnimatePresence mode="wait">
              {/* Step 1: Welcome */}
              {step === 1 && (
                <motion.div
                  key="step1"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="text-center"
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', delay: 0.1 }}
                    className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-primary/20 flex items-center justify-center"
                  >
                    <Sparkles className="w-10 h-10 text-primary" />
                  </motion.div>
                  
                  <h2 className="text-3xl font-bold mb-4">
                    {t('onboarding.welcome')}
                  </h2>
                  
                  <p className="text-lg text-white/70 mb-8 max-w-md mx-auto">
                    {t('onboarding.welcomeDesc')}
                  </p>

                  <div className="grid grid-cols-3 gap-4 mb-8">
                    {[
                      { icon: '🏃', labelKey: 'onboarding.sessionsCount' },
                      { icon: '📍', labelKey: 'onboarding.nearYou' },
                      { icon: '⭐', labelKey: 'onboarding.certifiedCoaches' }
                    ].map((item, idx) => (
                      <motion.div
                        key={idx}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 + idx * 0.1 }}
                        className="p-4 rounded-xl bg-white/5 border border-white/10"
                      >
                        <div className="text-2xl mb-2">{item.icon}</div>
                        <div className="text-sm text-white/70">{t(item.labelKey)}</div>
                      </motion.div>
                    ))}
                  </div>

                  <Button
                    onClick={handleNext}
                    className="btn-primary px-8 py-6 text-lg"
                    data-testid="onboarding-next-1"
                  >
                    Commencer
                    <ChevronRight className="w-5 h-5 ml-2" />
                  </Button>
                </motion.div>
              )}

              {/* Step 2: Objective Selection */}
              {step === 2 && (
                <motion.div
                  key="step2"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                >
                  <div className="text-center mb-8">
                    <h2 className="text-2xl font-bold mb-2">
                      {t('onboarding.objectiveTitle')}
                    </h2>
                    <p className="text-white/60">
                      {t('onboarding.objectiveDesc')}
                    </p>
                  </div>

                  <div className="space-y-3 mb-8">
                    {objectivesDef.map((objective, idx) => {
                      const isSelected = selectedObjective === objective.id;
                      const colors = colorClasses[objective.color];

                      return (
                        <motion.button
                          key={objective.id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ delay: idx * 0.05 }}
                          onClick={() => handleObjectiveSelect(objective.id)}
                          className={`w-full p-4 rounded-xl border-2 transition-all text-left flex items-center gap-4 ${
                            isSelected 
                              ? `${colors.selected} shadow-lg ${colors.glow}` 
                              : `${colors.bg} ${colors.border} hover:border-white/30`
                          }`}
                          data-testid={`onboarding-objective-${objective.id}`}
                        >
                          <div className={`text-3xl`}>{objective.emoji}</div>
                          <div className="flex-1">
                            <div className={`font-semibold ${isSelected ? colors.text : ''}`}>
                              {t(objective.labelKey)}
                            </div>
                            <div className="text-sm text-white/50">{t(objective.descKey)}</div>
                          </div>
                          {isSelected && (
                            <motion.div
                              initial={{ scale: 0 }}
                              animate={{ scale: 1 }}
                              className={`w-6 h-6 rounded-full ${colors.text} bg-current/20 flex items-center justify-center`}
                            >
                              <Check className="w-4 h-4 text-white" />
                            </motion.div>
                          )}
                        </motion.button>
                      );
                    })}
                  </div>

                  <div className="flex gap-3">
                    <Button
                      onClick={handleBack}
                      variant="outline"
                      className="flex-1 py-6"
                    >
                      Retour
                    </Button>
                    <Button
                      onClick={handleComplete}
                      className="btn-primary flex-1 py-6"
                      disabled={!selectedObjective || isRequestingLocation}
                      data-testid="onboarding-complete"
                    >
                      {isRequestingLocation ? (
                        <>
                          <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                          {t('onboarding.locating')}
                        </>
                      ) : (
                        <>
                          <MapPin className="w-5 h-5 mr-2" />
                          {t('onboarding.discover')}
                        </>
                      )}
                    </Button>
                  </div>

                  <p className="text-center text-white/40 text-xs mt-4">
                    {t('onboarding.locationNote')}
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
